/* Size help: exact variant IDs, explicit catalogue dimensions, closed by default. */
(() => {
  const fold = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[’']/g, ' ').trim();
  const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const id = value => String(value || '').split('/').pop();
  const sizeKey = value => String(value).toUpperCase().replace(/[\/–—]/g, '-').replace(/\s+/g, '');
  const coreSizes = {'XS-S':'48–54 cm','S-M':'55–58 cm','L-XL':'59–61 cm'};
  const aliases = {
    height:['hauteur totale','total height'],
    deckLength:['longueur du deck','longueur deck','deck length','longueur du plateau'],
    deckWidth:['largeur du deck','largeur deck','deck width','largeur du plateau'],
    barHeight:['hauteur du guidon','hauteur guidon','bar height'],
    barWidth:['largeur du guidon','largeur guidon','bar width'],
    weight:['poids','poids total','weight'],
    diameter:['diametre des roues','diametre roue','diametre de la roue','wheel diameter'],
    width:['largeur des roues','largeur roue','largeur de la gomme','largeur de roue','wheel width'],
    axle:['diametre de l axe','diametre axe','axle diameter']
  };
  function category(p) {
    const type = fold(p.product_type), title = fold(p.title), s = type + ' ' + title;
    if (/housse|mousse de|visserie|sac pour|carte cadeau/.test(s)) return '';
    if (/casque|helmet/.test(s)) return 'helmet';
    if (/genouill|coudier|protege.poignet|protection/.test(type) || /genouill|coudier|protege.poignet/.test(title)) return 'protection';
    if (/roue|wheel/.test(type) || /^(roues?|wheel)\b/.test(title)) return 'wheel';
    if (/deck|plateau de skate/.test(type) || /^(deck|plateau)\b/.test(title)) return 'deck';
    if (/guidon|handlebar/.test(type) || /^guidon\b/.test(title)) return 'bar';
    if (/trottinette/.test(type) && !/piece|fourche|collier|roulement|grip|poignee/.test(type)) return 'scooter';
    if (/roller|patin|chaussure/.test(type) && !/piece|roue|roulement|platine|frein|lacets/.test(type)) return 'footwear';
    if (/t.shirt|sweat|vetement|hoodie|pantalon/.test(s)) return 'clothing';
    return 'other';
  }
  function sheetRows(sheet) {
    return (sheet?.groups || []).flatMap(group => group.items || []).concat(sheet?.items || []);
  }
  function values(rows) {
    const result = {};
    for (const [key, names] of Object.entries(aliases)) {
      const matches = rows.filter(r => names.includes(fold(r.label))).map(r => String(r.value || '').trim()).filter(Boolean);
      const unique = [...new Set(matches)];
      if (unique.length === 1 && /\d/.test(unique[0]) && /\b(mm|cm|m|kg|g)\b|[″"]/.test(unique[0])) result[key] = unique[0];
    }
    return result;
  }
  function cm(value) {
    const m = String(value || '').match(/^(\d+(?:[.,]\d+)?)\s*(mm|cm)$/);
    if (!m) return value || '';
    return String(Math.round(Number(m[1].replace(',', '.')) * (m[2] === 'mm' ? 0.1 : 1) * 100) / 100).replace('.', ',') + ' cm';
  }
  function pair(a, b) { return a && b ? cm(a).replace(/ cm$/, '') + ' × ' + cm(b) : ''; }
  function model(p) {
    const kind = category(p);
    const options = p.options || [];
    const indexes = options.map((o, i) => /^(taille|size|pointure|longueur|largeur|diametre|dimensions?)$/.test(fold(o.name)) ? i : -1).filter(i => i >= 0);
    if (!kind || !indexes.length) return null;
    const sheet = p.technical_sheet || {};
    const commonRows = sheetRows(sheet), common = values(commonRows);
    const guide = commonRows.filter(r => /guide des tailles|size guide|tour de tete|tour de jambe|tour de bras/.test(fold(r.label))).map(r => String(r.value || '')).filter(Boolean);
    const specificKeys = new Set((sheet.variants || []).flatMap(v => Object.keys(values(v.items || []))));
    const columns = [], seen = new Set();
    for (const variant of p.variants || []) {
      const match = (sheet.variants || []).find(v => id(v.variant_id) === id(variant.id));
      const specific = values(match?.items || []);
      // Never use another size's measurements when this variant is undocumented.
      const facts = {...common};
      for (const key of specificKeys) delete facts[key];
      Object.assign(facts, specific);
      const label = indexes.map(i => variant.options?.[i]).filter(Boolean).join(' / ');
      const signature = JSON.stringify([label, facts]);
      if (!label || seen.has(signature)) continue;
      seen.add(signature);
      columns.push({label, facts, variantId:id(variant.id)});
    }
    const present = key => columns.some(c => c.facts[key]);
    let rows = [];
    const add = (label, key) => { if (present(key)) rows.push({label, values:columns.map(c => cm(c.facts[key]))}); };
    if (kind === 'scooter') add('height', 'height');
    if (['scooter','deck'].includes(kind)) {
      if (columns.some(c => c.facts.deckLength && c.facts.deckWidth)) rows.push({label:'deck', values:columns.map(c => pair(c.facts.deckLength,c.facts.deckWidth))});
      else { add('deck_length','deckLength'); add('deck_width','deckWidth'); }
    }
    if (['scooter','bar'].includes(kind)) {
      if (columns.some(c => c.facts.barHeight && c.facts.barWidth)) rows.push({label:'bar',values:columns.map(c => pair(c.facts.barHeight,c.facts.barWidth))});
      else { add('bar_height','barHeight'); add('bar_width','barWidth'); }
    }
    if (kind === 'wheel') for (const key of ['diameter','width','axle']) if(present(key)) rows.push({label:key,values:columns.map(c => c.facts[key])});
    if (['scooter','deck','bar','wheel'].includes(kind) && present('weight')) rows.push({label:'weight',values:columns.map(c => {
      const value = c.facts.weight || '', grams = value.match(/^(\d+(?:[.,]\d+)?)\s*g$/);
      return grams && Number(grams[1].replace(',', '.')) >= 1000 ? String(Number(grams[1].replace(',', '.')) / 1000).replace('.', ',') + ' kg' : value;
    })});
    if (kind === 'helmet') {
      const knownCore = /\bcore\b/.test(fold(p.title)) && /\baction\b/.test(fold(p.title));
      const parsed = typeof window !== 'undefined' ? window.CabaneProductData?.helmetSizeGuide(p) || [] : [];
      const head = columns.map(c => knownCore ? coreSizes[sizeKey(c.label)] || '' : parsed.find(r => sizeKey(r.size) === sizeKey(c.label))?.value || '');
      if(head.some(Boolean)) rows = [{label:'head', values:head}];
    }
    return {kind,columns,rows,guide,gravis:/^striker-trottinette-freestyle-gravis-s(?:-noir)?$/.test(p.handle || '')};
  }
  function render(p, t) {
    const m = model(p); if (!m) return '';
    const text = key => esc(t[key] || key);
    const table = m.rows.length && m.columns.length ? `<div class="cabane-fit__scroll" tabindex="0" role="region" aria-label="${text('comparison')}"><table class="cabane-fit__table"><caption>${text('comparison')}</caption><thead><tr><th scope="col">${text('dimension')}</th>${m.columns.map(c => `<th scope="col">${esc(c.label)}</th>`).join('')}</tr></thead><tbody>${m.rows.map(row => `<tr><th scope="row">${text(row.label)}</th>${row.values.map(v => `<td>${esc(v || '—')}</td>`).join('')}</tr>`).join('')}</tbody></table></div>` : '';
    const actualGuide = m.guide.map(g => `<p>${esc(g)}</p>`).join('');
    const missing = !table && !actualGuide ? `<p class="cabane-fit__muted">${text('missing')}</p>` : '';
    const advice = m.gravis ? ['small_advice','large_advice','park_advice'] : [m.kind + '_advice'];
    return `<details class="cabane-fit__disclosure"><summary><span>${text('guide_title')}</span><span class="cabane-fit__symbol" aria-hidden="true"></span></summary><div class="cabane-fit__content">${table}${actualGuide}${missing}<p class="cabane-fit__label">${text('measure')}</p><p>${text(m.kind + '_measure')}</p>${m.kind === 'scooter' ? `<p>${text('fit')}</p>` : ''}<p class="cabane-fit__label">${text('practice')}</p>${advice.map(k => `<p>${text(k)}</p>`).join('')}</div></details>`;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {model,render};
  if (typeof window === 'undefined' || customElements.get('cabane-fit-guide')) return;
  customElements.define('cabane-fit-guide', class extends HTMLElement {
    connectedCallback() {
      const init = () => {
        if (!this.isConnected || this.dataset.ready) return;
        const source = this.querySelector('script[data-fit-config]'); if (!source) return;
        try {
          const {product,labels} = JSON.parse(source.textContent);
          const html = render(product,labels);
          if (!html) return;
          this.innerHTML = html; this.hidden = false; this.dataset.ready = 'true';
        } catch (_) { /* Leave the existing product selector usable if data is malformed. */ }
      };
      if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded',init,{once:true}); else init();
    }
  });
})();
